/**
 * Store de gestion des matériaux et couleurs
 * Gère les matériaux disponibles et les sélections actuelles
 */

import { defineStore } from 'pinia';
import type { MaterialConfig, MaterialEntry } from '../types';
import { dataService } from '../services/DataService';
import { excludeWindowMaterials, enrichMaterialsWithLabels } from '../utils/materialUtils';

// Ordre d'affichage personnalisé pour les teintes
const UNI_COLOR_ORDER = ['BC_ZHJ', 'BC_WBF', 'BC_WB3', 'BC_WA1', 'BC_WBH', 'BC_WBE']
const SOCOLOR_COLOR_ORDER = ['BC_E86', 'BC_FDC', 'BC_FFZ', 'BC_E5G', 'BC_E85']

// Mapping couleur extérieure → intérieur automatique
// INT-INTBASE_001 = Brown, INT-INTBASE_002 = Black
const EXTERIOR_TO_INTERIOR_MAPPING: Record<string, string> = {
  'BC_ZHJ': 'INT-INTBASE_002',  // Arctic White Pearl → Black
  'BC_WA1': 'INT-INTBASE_001',  // Land Breeze Green Pearl Metallic → Brown
  'BC_WB3': 'INT-INTBASE_002',  // Bluish Black Pearl → Black
  'BC_WBE': 'INT-INTBASE_001',  // Splendid Silver Pearl Metallic → Brown
  'BC_WBF': 'INT-INTBASE_002',  // Grandeur Grey Pearl Metallic → Black
  'BC_WBH': 'INT-INTBASE_002',  // Celestial Blue Pearl Metallic → Black
  'BC_E5G': 'INT-INTBASE_002',  // Arctic White Pearl / Bluish Black Pearl → Black
  'BC_FDC': 'INT-INTBASE_001',  // Land Breeze Green Pearl Metallic / Bluish Black Pearl → Brown
  'BC_E85': 'INT-INTBASE_001',  // Splendid Silver Pearl Metallic / Bluish Black Pearl → Brown
  'BC_FFZ': 'INT-INTBASE_002',  // Grandeur Grey Pearl Metallic / Bluish Black Pearl → Black
  'BC_E86': 'INT-INTBASE_002',  // Opulent Red Pearl Metallic / Bluish Black Pearl → Black
}

/**
 * Extrait le code couleur (BC_XXX) depuis un ID de matériau
 */
const extractColorCodeFromMaterial = (materialId: string): string | null => {
  const match = materialId.match(/BC_[A-Z0-9]+/)
  return match ? match[0] : null
}

/**
 * Retourne l'intérieur correspondant à une couleur extérieure
 */
const getInteriorForExterior = (exteriorColorId: string): string | null => {
  const colorCode = extractColorCodeFromMaterial(exteriorColorId)
  if (!colorCode) return null
  return EXTERIOR_TO_INTERIOR_MAPPING[colorCode] || null
}

/**
 * Fonction de tri personnalisée selon l'ordre spécifié
 */
const sortByCustomOrder = <T extends { material: string }>(items: T[], order: string[]): T[] => {
  // Fonction pour extraire le code couleur (BC_XXX) du material
  const extractColorCode = (material: string): string => {
    const match = material.match(/BC_[A-Z0-9]+/)
    return match ? match[0] : material
  }
  
  return [...items].sort((a, b) => {
    const colorCodeA = extractColorCode(a.material)
    const colorCodeB = extractColorCode(b.material)
    
    const indexA = order.indexOf(colorCodeA)
    const indexB = order.indexOf(colorCodeB)
    
    // Les éléments dans l'ordre ont priorité
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB
    }
    // Les éléments dans l'ordre viennent en premier
    if (indexA !== -1) return -1
    if (indexB !== -1) return 1
    // Sinon, trier alphabétiquement
    return colorCodeA.localeCompare(colorCodeB)
  })
}

/**
 * État du store des matériaux
 */
interface MaterialState {
  /** Matériaux disponibles pour le grade actuel */
  availableMaterials: MaterialConfig | null;
  /** Couleur extérieure sélectionnée */
  selectedExteriorColor: string | null;
  /** Couleur intérieure sélectionnée */
  selectedInteriorColor: string | null;
  /** État de chargement */
  isLoading: boolean;
  /** Erreur actuelle */
  error: string | null;
  /** Groupes d'accessoires optionnels */
  accessories: AccessoryGroup[];
}

/**
 * Type combiné état + getters pour typer `this` dans les getters Pinia
 */
type MaterialStoreThis = MaterialState & {
  exteriorColors: MaterialEntry[];
  exteriorUniColors: MaterialEntry[];
  exteriorSocolorColors: MaterialEntry[];
  interiorColors: MaterialEntry[];
  selectedMaterials: { exterior: string | null; interior: string | null };
  accessoryMaterials: MaterialEntry[];
  groupedAccessories: Map<string, MaterialEntry[]>;
  activeAccessories: MaterialEntry[];
};

interface AccessoryGroup {
  type: string // 'Wheel', 'Lamp', 'Rack', etc.
  versions: MaterialEntry[] // Toutes les versions de cet accessoire
  selectedVersion: string | null // Matériau sélectionné
  isActive: boolean // Affiché ou non
}

/**
 * Store de gestion des matériaux
 */
export const useMaterialStore = defineStore('material', {
  state: (): MaterialState => ({
    availableMaterials: null,
    selectedExteriorColor: null,
    selectedInteriorColor: null,
    isLoading: false,
    error: null,
    accessories: []
  }),

  getters: {
    /**
     * Retourne les couleurs extérieures disponibles (layer 0 + BC_)
     * Triées selon l'ordre UNI_COLOR_ORDER pour assurer une sélection par défaut cohérente
     */
    exteriorColors: (state): MaterialEntry[] => {
      if (!state.availableMaterials) return [];
      // Garder seulement les couleurs de layer 0 avec BC_
      const filtered = state.availableMaterials.MATERIALS.EXT.filter(m =>
        m.layer === 0 && m.material.includes('-BC_')
      );
      // Appliquer le tri personnalisé
      return sortByCustomOrder(filtered, UNI_COLOR_ORDER);
    },

    /**
     * Retourne les couleurs extérieures de type uni (teinteType === 'uni')
     */
    exteriorUniColors: (state): MaterialEntry[] => {
      if (!state.availableMaterials) return [];
      const filtered = state.availableMaterials.MATERIALS.EXT.filter(m =>
        m.layer === 0 && m.material.includes('-BC_') && m.teinteType === 'uni'
      );
      return sortByCustomOrder(filtered, UNI_COLOR_ORDER);
    },

    /**
     * Retourne les couleurs extérieures de type socolor (teinteType === 'socolor')
     */
    exteriorSocolorColors: (state): MaterialEntry[] => {
      if (!state.availableMaterials) return [];
      const filtered = state.availableMaterials.MATERIALS.EXT.filter(m =>
        m.layer === 0 && m.material.includes('-BC_') && m.teinteType === 'socolor'
      );
      return sortByCustomOrder(filtered, SOCOLOR_COLOR_ORDER);
    },

    /**
     * Retourne les couleurs intérieures disponibles
     */
    interiorColors: (state): MaterialEntry[] => {
      if (!state.availableMaterials) return [];
      return state.availableMaterials.MATERIALS.INT;
    },

    /**
     * Retourne les matériaux sélectionnés
     */
    selectedMaterials: (state) => ({
      exterior: state.selectedExteriorColor,
      interior: state.selectedInteriorColor
    }),

    /**
     * Computed - Filtrer les accessoires depuis TOUS les matériaux EXT (sans les couleurs de base ni les windows)
     * Note: Ce getter est utilisé par le viewer 3D pour afficher les matériaux Lamp, Wheel, Garnish
     * ColorContent.vue utilise exteriorColors pour les teintes, ce qui est correct
     */
    accessoryMaterials: (state): MaterialEntry[] => {
      if (!state.availableMaterials) return [];
      
      // Partir de TOUS les matériaux EXT, pas juste exteriorColors
      const allExtMaterials = state.availableMaterials.MATERIALS.EXT;
      
      // Filtrer les accessoires (commencent par EXT- mais pas EXT-GRD)
      const accessories = allExtMaterials.filter((m: MaterialEntry) =>
        m.material.startsWith('EXT-') && !m.material.startsWith('EXT-GRD')
      );
      
      // Exclure les matériaux windows
      return excludeWindowMaterials(accessories);
    },

    /**
     * Computed - Grouper par type d'accessoire
     */
    groupedAccessories: function(this: MaterialStoreThis): Map<string, MaterialEntry[]> {
      const groups: Map<string, MaterialEntry[]> = new Map()
      
      // Utiliser this car les getters sont accessibles via this dans Pinia
      this.accessoryMaterials.forEach((m: MaterialEntry) => {
        // Extraire le type depuis EXT-{Type}_{NUM}
        const match = m.material.match(/^EXT-([^_]+)/)
        if (match) {
          const type = match[1]
          if (!groups.has(type)) groups.set(type, [])
          groups.get(type)!.push(m)
        }
      })
      
      // Trier chaque groupe par numéro de version
      groups.forEach((versions) => {
        versions.sort((a, b) => a.material.localeCompare(b.material))
      })
      
      return groups
    },

    /**
     * Computed - Accessoires actifs pour l'affichage
     */
    activeAccessories: (state) => {
      console.log('[DEBUG materialStore.activeAccessories] state =', state);
      console.log('[DEBUG materialStore.activeAccessories] state.accessories =', state.accessories);

      const accessories = state.accessories || [];
      return accessories
        .filter((a: AccessoryGroup) => a.isActive && a.selectedVersion !== null)
        .map((a: AccessoryGroup) => {
          const version = a.versions.find(v => v.material === a.selectedVersion!);
          return version ?? null;
        })
        .filter((version): version is MaterialEntry => version !== null);
    }
  },

  actions: {
    /**
     * Charge les matériaux pour un grade spécifique
     * @param gradeId ID du grade pour lequel charger les matériaux
     * @param previousColorId Couleur précédemment sélectionnée (optionnel) - si disponible dans le nouveau grade, elle sera conservée
     */
    async fetchMaterials(gradeId: string, previousColorId?: string): Promise<void> {
      this.isLoading = true;
      this.error = null;

      try {
        // S'assurer que le grade est chargé dans le DataService
        if (!dataService.isGradeLoaded() || dataService.getCurrentGradeId() !== gradeId) {
          await dataService.setCurrentGrade(gradeId);
        }

        // Récupérer les matériaux et les items
        const materials = dataService.getMaterials();
        const items = dataService.getItems();

        // Enrichir les matériaux avec les labels depuis les items
        const enrichedMaterials: MaterialConfig = {
          MATERIALS: {
            EXT: enrichMaterialsWithLabels(materials.MATERIALS.EXT, items),
            INT: enrichMaterialsWithLabels(materials.MATERIALS.INT, items)
          }
        };

        this.availableMaterials = enrichedMaterials;

        // Obtenir les couleurs extérieures triées
        const exteriorColors = this.exteriorColors;
        
        if (exteriorColors.length > 0) {
          // Log pour diagnostic
          console.log(`[MaterialStore] Couleurs disponibles pour ${gradeId}:`, exteriorColors.map(c => c.material));
          console.log(`[MaterialStore] Couleur précédemment sélectionnée: ${previousColorId || 'aucune'}`);
          
          // Déterminer la couleur à sélectionner
          let colorToSelect: string | null = null;
          
          // 1. Vérifier si la couleur précédemment sélectionnée est disponible
          if (previousColorId) {
            const previousColorAvailable = exteriorColors.some(c => c.material === previousColorId);
            if (previousColorAvailable) {
              colorToSelect = previousColorId;
              console.log(`[MaterialStore] Couleur précédente conservée: ${colorToSelect}`);
            } else {
              console.log(`[MaterialStore] Couleur précédente ${previousColorId} non disponible dans ${gradeId}`);
            }
          }
          
          // 2. Si pas de couleur précédente ou non disponible, sélectionner BC_ZHJ par défaut
          if (!colorToSelect) {
            const zhjColor = exteriorColors.find(c =>
              c.material.includes('BC_ZHJ') || c.material === 'BC_ZHJ'
            );
            if (zhjColor) {
              colorToSelect = zhjColor.material;
              console.log(`[MaterialStore] Couleur par défaut BC_ZHJ sélectionnée: ${colorToSelect}`);
            } else {
              // Fallback: première couleur si BC_ZHJ n'existe pas
              colorToSelect = exteriorColors[0].material;
              console.log(`[MaterialStore] Première couleur sélectionnée: ${colorToSelect}`);
            }
          }
          
          this.selectedExteriorColor = colorToSelect;

          // Sélection automatique de l'intérieur basée sur le mapping couleur → intérieur
          const mappedInterior = getInteriorForExterior(colorToSelect);
          const interiorColors = this.availableMaterials?.MATERIALS.INT || [];
          if (mappedInterior && interiorColors.some(c => c.material === mappedInterior)) {
            this.selectedInteriorColor = mappedInterior;
            console.log(`[MaterialStore] Intérieur automatique initial: ${mappedInterior} pour ${colorToSelect}`);
          } else if (interiorColors.length > 0) {
            // Fallback si pas de mapping trouvé
            this.selectedInteriorColor = interiorColors[0].material;
            console.log('Auto-sélection première couleur intérieure (fallback):', interiorColors[0].material);
          }
        } else {
          // Aucune couleur extérieure disponible, sélection intérieure par défaut
          const interiorColors = this.availableMaterials?.MATERIALS.INT || [];
          if (interiorColors.length > 0 && this.selectedInteriorColor === null) {
            console.log('Auto-sélection première couleur intérieure:', interiorColors[0].material);
            this.selectedInteriorColor = interiorColors[0].material;
          }
        }

        // Initialiser les accessoires après le chargement des matériaux
        this.initAccessories();

      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Erreur lors du chargement des matériaux';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Sélectionne un matériau (couleur)
     * @param materialId ID du matériau à sélectionner
     * @param type Type de matériau ('exterior' ou 'interior')
     */
    selectMaterial(materialId: string, type: 'exterior' | 'interior'): void {
      if (type === 'exterior') {
        this.selectedExteriorColor = materialId;
      } else if (type === 'interior') {
        this.selectedInteriorColor = materialId;
      }
    },

    /**
     * Sélectionne une couleur extérieure et met à jour automatiquement l'intérieur correspondant
     * @param colorId ID de la couleur extérieure
     */
    selectExteriorColor(colorId: string): void {
      this.selectedExteriorColor = colorId;

      // Sélection automatique de l'intérieur basée sur le mapping couleur → intérieur
      const mappedInterior = getInteriorForExterior(colorId);
      if (mappedInterior) {
        // Vérifier que l'intérieur mappé est disponible
        const interiorColors = this.availableMaterials?.MATERIALS.INT || [];
        const interiorAvailable = interiorColors.some(c => c.material === mappedInterior);
        if (interiorAvailable) {
          this.selectedInteriorColor = mappedInterior;
          console.log(`[MaterialStore] Intérieur automatique sélectionné: ${mappedInterior} pour ${colorId}`);
        }
      }
    },

    /**
     * Sélectionne une couleur intérieure
     * @param colorId ID de la couleur intérieure
     */
    selectInteriorColor(colorId: string): void {
      this.selectedInteriorColor = colorId;
    },

    /**
     * Vérifie si une couleur est disponible
     * @param colorId ID de la couleur à vérifier
     * @param type Type de couleur ('exterior' ou 'interior')
     */
    isColorAvailable(colorId: string, type: 'exterior' | 'interior'): boolean {
      const colors = type === 'exterior' ? this.exteriorColors : this.interiorColors;
      return colors.some(material => material.material === colorId);
    },

    /**
     * Réinitialise les sélections de matériaux
     */
    resetSelections(): void {
      this.selectedExteriorColor = null;
      this.selectedInteriorColor = null;
    },

    /**
     * Vide les matériaux disponibles
     */
    clearMaterials(): void {
      this.availableMaterials = null;
      this.selectedExteriorColor = null;
      this.selectedInteriorColor = null;
      this.error = null;
      this.accessories = [];
    },

    // Actions pour accessoires
    initAccessories() {
      // Récupérer les items pour connaître les checked/visible par défaut
      let itemsConfig: ReturnType<typeof dataService.getItems> | null = null;
      try {
        itemsConfig = dataService.getItems();
      } catch (error) {
        console.warn('[MaterialStore] Impossible de récupérer les items pour initAccessories');
      }

      this.accessories = Array.from(this.groupedAccessories.entries()).map(([type, versions]) => {
        // Chercher la version checked par défaut dans les items
        let defaultVersion = versions[0]?.material || null;
        let isActiveByDefault = true;

        if (itemsConfig) {
          // Chercher dans les items la version avec checked: true
          for (const category of itemsConfig.ITEMS) {
            for (const group of category.groups) {
              // Trouver un item qui correspond à ce type d'accessoire et qui est checked
              const checkedItem = group.items.find(item => {
                // Vérifier si cet item correspond à une des versions
                return versions.some(v => v.material.includes(item.item_id)) && item.checked;
              });

              if (checkedItem) {
                // Trouver la version correspondante
                const matchingVersion = versions.find(v => v.material.includes(checkedItem.item_id));
                if (matchingVersion) {
                  defaultVersion = matchingVersion.material;
                  isActiveByDefault = checkedItem.visible !== false;
                  console.log(`[MaterialStore] Accessoire ${type}: version par défaut = ${checkedItem.item_id}, actif = ${isActiveByDefault}`);
                }
                break;
              }

              // Si aucun item n'est checked, vérifier si tous sont non-visibles (désactiver l'accessoire)
              const allItemsForType = group.items.filter(item =>
                versions.some(v => v.material.includes(item.item_id))
              );
              if (allItemsForType.length > 0 && allItemsForType.every(item => !item.visible && !item.checked)) {
                isActiveByDefault = false;
              }
            }
          }
        }

        return {
          type,
          versions,
          selectedVersion: defaultVersion,
          isActive: isActiveByDefault
        };
      });
    },

    toggleAccessory(type: string) {
      const acc = this.accessories.find(a => a.type === type)
      if (acc) acc.isActive = !acc.isActive
    },

    selectAccessoryVersion(type: string, directory: string) {
      const acc = this.accessories.find(a => a.type === type)
      if (acc && acc.versions.some(v => v.material === directory)) {
        acc.selectedVersion = directory
      }
    }
  }
});
